import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  UserGroupIcon, 
  DashboardSquare01Icon, 
  Home01Icon,
  Clock01Icon,
  Money01Icon,
  Menu01Icon,
  UserCircleIcon,
  Logout01Icon,
  ArrowUpRight01Icon
} from '@hugeicons/core-free-icons';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveModal from './ResponsiveModal';

const BottomNavbar = ({ pendingCount = 0, payoutCount = 0, rewardsCount = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const clientNavItems = [
    { name: 'Home', path: '/', icon: Home01Icon },
    { name: 'Orders', path: '/requests', icon: DashboardSquare01Icon },
    { name: 'More', isMore: true, icon: Menu01Icon },
  ];

  const agentNavItems = [
    { name: 'Dash', path: '/dashboard', icon: DashboardSquare01Icon },
    { name: 'Orders', path: '/requests', icon: Home01Icon },
    { name: 'Clients', path: '/clients', icon: UserGroupIcon },
    { name: 'More', isMore: true, icon: Menu01Icon },
  ];

  const moreItems = user?.role === 'agent' ? [
    { name: 'Payouts', path: '/commissions', icon: Money01Icon, badge: payoutCount },
    { name: 'History', path: '/history', icon: Clock01Icon },
    { name: 'Profile', path: '/profile', icon: UserCircleIcon },
  ] : [
    { name: 'Rewards', path: '/commissions', icon: Money01Icon, badge: rewardsCount },
    { name: 'History', path: '/history', icon: Clock01Icon },
    { name: 'Profile', path: '/profile', icon: UserCircleIcon },
  ];

  const moreNotificationCount = user?.role === 'agent' ? payoutCount : rewardsCount;
  const showMoreDot = moreNotificationCount > 0;

  const navItems = user?.role === 'agent' ? agentNavItems : clientNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isNewFeatureVisible = new Date() <= new Date('2026-03-31');

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md border-t border-light-gray/20 dark:border-dark-border/20 z-50 pb-safe">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-around h-16">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isOrdersTab = item.name === 'Orders';
              
              const content = (
                <div className={`p-2 rounded-2xl transition-all duration-300 relative ${isActive ? 'bg-soft-green/10 scale-110' : ''}`}>
                  <HugeiconsIcon icon={Icon} size={22} />
                  {isOrdersTab && pendingCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-black text-white shadow-md border border-white dark:border-dark-surface">
                      {pendingCount}
                    </span>
                  )}
                </div>
              );

              if (item.isMore) {
                return (
                  <button
                    key={item.name}
                    onClick={() => setIsMoreOpen(true)}
                    className="flex flex-col items-center justify-center w-full pt-1 text-sm font-bold text-muted-gray hover:text-charcoal dark:hover:text-white transition-colors group relative"
                  >
                    <div className="p-2 rounded-2xl transition-all duration-300 relative">
                      <HugeiconsIcon icon={Icon} size={22} />
                      {showMoreDot && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 border-2 border-white dark:border-dark-surface">
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] mt-0.5 font-bold">{item.name}</span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center justify-center w-full pt-1 text-sm font-bold transition-colors group relative ${
                    isActive
                      ? 'text-soft-green'
                      : 'text-muted-gray hover:text-charcoal dark:hover:text-white'
                  }`}
                >
                  {content}
                  <span className={`text-[10px] mt-0.5 font-bold transition-colors ${isActive ? 'text-soft-green' : 'text-muted-gray'}`}>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      <ResponsiveModal isOpen={isMoreOpen} onClose={() => setIsMoreOpen(false)}>
        <div className="py-6 space-y-2">
          {moreItems.map((item) => {
            const Icon = item.icon;
            const hasBadge = item.badge > 0;
            
            return (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.path);
                  setIsMoreOpen(false);
                }}
                className="w-full flex items-center gap-4 p-5 bg-gray-50 dark:bg-white/5 rounded-3xl hover:bg-soft-green/10 hover:text-soft-green transition-all group relative"
              >
                <div className="p-3 bg-white dark:bg-black/20 rounded-2xl group-hover:bg-soft-green/20 transition-all">
                  <HugeiconsIcon icon={Icon} size={24} />
                </div>
                <div className="flex flex-col items-start">
                  <span className="font-black text-sm uppercase tracking-widest">{item.name}</span>
                  {isNewFeatureVisible && (item.name === 'Rewards' || item.name === 'Payouts') && (
                    <span className="bg-soft-green text-white text-[8px] font-black px-1.5 py-0.5 rounded-full mt-1 animate-pulse">NEW</span>
                  )}
                </div>
                {hasBadge && (
                  <span className="absolute right-16 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-black text-white shadow-md border-2 border-white dark:border-dark-surface">
                    {item.badge}
                  </span>
                )}
                <HugeiconsIcon icon={ArrowUpRight01Icon} size={18} className="ml-auto opacity-30" />
              </button>
            );
          })}
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 p-5 bg-red-50 dark:bg-red-900/10 rounded-3xl text-red-500 mt-4 transition-all"
          >
            <div className="p-3 bg-white dark:bg-black/20 rounded-2xl">
              <HugeiconsIcon icon={Logout01Icon} size={24} />
            </div>
            <span className="font-black text-sm uppercase tracking-widest">Logout</span>
          </button>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default BottomNavbar;
